import _ from 'lodash';
import { isObject, orderBy } from '@skyjs/lodash-extras';
import { createTester } from '@skyjs/filter';
import flatMapArrayLike from './flatMapArrayLike.js';
import Grouping from './Grouping.js';
import getData from './getData.js';
import { DateTime, Time, UTCDate } from '@skyjs/date';

export default class Collection extends Array {
  static from(itemOrArray) {
    if (_.isNil(itemOrArray)) {
      return new Collection();
    }
    return Collection.fromArray(_.castArray(itemOrArray));
  }

  static fromArray(items) {
    if (_.isNil(items)) {
      return new Collection();
    }
    const col = new Collection(items.length);
    let index = -1;
    while (++index < items.length) {
      col[index] = items[index];
    }
    return col;
  }

  get ids() {
    return this.map('id').compact();
  }

  get isEmpty() {
    return this.length === 0;
  }

  get first() {
    return _.first(this);
  }

  get last() {
    return _.last(this);
  }

  add(obj) {
    const col = Collection.fromArray(this);
    col.push(obj);
    return col;
  }

  at(index) {
    return this[index];
  }

  compact() {
    return Collection.fromArray(_.compact(this));
  }

  compactMap(fnOrProp) {
    return Collection.fromArray(_.compact(_.map(this, fnOrProp)));
  }

  concat(other) {
    return Collection.fromArray(_.concat(this, other));
  }

  every(smartPredicate) {
    return _.every(this, getPredicate(smartPredicate, this, 'every'));
  }

  filter(smartPredicate) {
    return Collection.fromArray(_.filter(this, getPredicate(smartPredicate, this, 'filter')));
  }

  find(smartPredicate) {
    return _.find(this, getPredicate(smartPredicate, this, 'find'));
  }

  findIndex(smartPredicate) {
    return _.findIndex(this, getPredicate(smartPredicate, this, 'findIndex'));
  }

  flatMap(fnOrProp) {
    return Collection.fromArray(flatMapArrayLike(this, fnOrProp));
  }

  flatten() {
    return Collection.fromArray(_.flatten(this));
  }

  forEach(fn) {
    _.forEach(this, fn);
  }

  get(smartPredicate) {
    return _.find(this, getPredicate(smartPredicate, this, 'get'));
  }

  groupBy(prop) {
    const groups = _.groupBy(this, prop);
    if (_.get(_.keys(groups), '0') === 'undefined') return new Grouping({});
    return new Grouping(groups);
  }

  has(smartPredicate) {
    return _.some(this, getPredicate(smartPredicate, this, 'has'));
  }

  indexOf(value) {
    return _.indexOf(this, value);
  }

  isEqual(other, comparator = _.isEqual) {
    if (this.length === 0 && other.length === 0) return true;
    if (this.length !== other.length) return false;
    return _.differenceWith(this, other, comparator).length === 0;
  }

  map(fnOrProp) {
    return Collection.fromArray(_.map(this, fnOrProp));
  }

  move(smartPredicate, index) {
    const item = this.get(smartPredicate);
    if (!item) return this;
    const col = this.remove(smartPredicate);
    col.splice(index, 0, item);
    return col;
  }

  orderBy(clauseOrFn, orders) {
    if (_.isFunction(clauseOrFn)) {
      return Collection.fromArray(_.orderBy(this, clauseOrFn, orders));
    }
    return Collection.fromArray(orderBy(this, clauseOrFn, orders));
  }

  reject(smartPredicate) {
    return Collection.fromArray(_.reject(this, getPredicate(smartPredicate, this, 'reject')));
  }

  reduce(fn, accumulator) {
    return _.reduce(this, fn, accumulator);
  }

  remove(smartPredicate) {
    return Collection.fromArray(_.reject(this, getPredicate(smartPredicate, this, 'remove')));
  }

  replace(obj) {
    const index = this.findIndex(obj);
    if (index === -1) {
      return this.add(obj);
    }
    const col = Collection.fromArray(this);
    col[index] = obj;
    return col;
  }

  reverse() {
    return Collection.fromArray([...this].reverse());
  }

  skip(number) {
    return Collection.fromArray(_.drop(this, number));
  }

  some(smartPredicate) {
    return _.some(this, getPredicate(smartPredicate, this, 'some'));
  }

  sum() {
    return _.sum(this);
  }

  sumBy(extractValue) {
    return _.sumBy(this, extractValue);
  }

  take(number) {
    return Collection.fromArray(_.take(this, number));
  }

  toArray() {
    return this;
  }

  uniq() {
    if (_.get(this, 'first.id') !== undefined) {
      return this.uniqBy('id');
    }

    return Collection.fromArray(_.uniq(this));
  }

  uniqBy(attr) {
    return Collection.fromArray(_.uniqBy(this, attr));
  }

  // Deprecated
  where(criteria) {
    return this.filter(criteria);
  }
}

// Liste des méthodes recevant un smart predicate et ayant besoin d'identifier un item en particulier.
const IDENTITY_METHODS = ['has', 'get', 'find', 'findIndex', 'remove'];

function getPredicate(smartPredicate, col, methodName) {
  if (col.length === 0) {
    // Sortir rapidement, predicate pas nécessaire.
    return () => false;
  }

  if (_.isFunction(smartPredicate)) {
    return smartPredicate;
  }

  if (_.isNil(smartPredicate)) {
    if (IDENTITY_METHODS.includes(methodName)) {
      return item => item === smartPredicate;
    }
    // filter et reject doivent retourner tous les items
    // some et every doivent retourner true
    return methodName === 'reject' ? () => false : () => true;
  }

  // Raccourci permettant de passer l'id directement au lieu de passer { id : 'guid' }.
  // S'applique uniquement aux méthodes ayant besoin d'identifier un item en particulier.
  if (_.isString(smartPredicate) && isObject(col.first) && IDENTITY_METHODS.includes(methodName)) {
    return item => _.get(item, 'id') === smartPredicate;
  }

  // Pratique pour toutes les méthodes (ex: requests.filter({ requester }))
  if (_.isString(smartPredicate.id)) {
    return item => _.get(item, 'id') === smartPredicate.id;
  }

  if (smartPredicate instanceof DateTime) {
    return obj => obj instanceof DateTime && smartPredicate.isEqual(obj);
  }

  if (smartPredicate instanceof UTCDate) {
    return obj => obj instanceof UTCDate && smartPredicate.isEqual(obj);
  }

  if (smartPredicate instanceof Time) {
    return obj => obj instanceof Time && smartPredicate.isEqual(obj);
  }

  if (!_.isObject(smartPredicate)) {
    return obj => obj === smartPredicate; // Si on retourne seulement smartPredicate, lodash va considérer '1' egal à 1, on ne veut pas ça
  }

  // TODO PL : Ne plus utiliser getData (relié à l'ancien modèle).
  // C'est normalizeCriteria contenu dans Db qu'il faut utiliser.
  // normalizeCriteria dans Db ne sera plus nécessaire.
  // À faire une fois toutes les applications converties au nouveau modèle et déployées.
  return createTester(getData(smartPredicate, { dateType: true, convertUndefinedToNull: true }));
}
